import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const SUBSCRIBED_TOPICS_KEY = 'fcm.subscribedTopics';
const LEGACY_SUBSCRIBED_TOPIC_KEY = 'fcm.subscribedTopic';
const LEGACY_CLEANUP_DONE_KEY = 'fcm.legacyCleanupDone';

export type NotificationPermission = 'granted' | 'denied' | 'prompt';

export async function getNotificationPermission(): Promise<NotificationPermission> {
  if (!Capacitor.isNativePlatform()) return 'granted';
  const { receive } = await PushNotifications.checkPermissions();
  if (receive === 'granted' || receive === 'denied') return receive;
  return 'prompt';
}

function loadSubscribedTopics(): Set<string> {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(SUBSCRIBED_TOPICS_KEY) ?? '[]');
    return new Set(
      Array.isArray(stored) ? stored.filter((topic): topic is string => typeof topic === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function saveSubscribedTopics(topics: Set<string>) {
  localStorage.setItem(SUBSCRIBED_TOPICS_KEY, JSON.stringify([...topics]));
}

/**
 * Before v3.8.5 every network opened subscribed the device without unsubscribing
 * from the previous one, and only the last topic was remembered. The leftovers
 * cannot be listed, so the whole Firebase installation is dropped: its token and
 * topics are invalidated and the next token starts without any topic.
 *
 * `FCM.refreshToken()` is not used: on iOS it deletes the data and fetches the
 * token in parallel, and can return the old one. On iOS `deleteInstance` may also
 * reject and then resolve, so success is a different token, not the promise state.
 * The marker is set on success only: a failed cleanup is retried at the next sync.
 */
async function cleanLegacySubscriptions(previousToken: string): Promise<void> {
  if (localStorage.getItem(LEGACY_CLEANUP_DONE_KEY)) return;

  try {
    await FCM.deleteInstance();
  } catch (error) {
    console.error('FCM instance deletion error:', error);
  }
  const { token } = await FCM.getToken();
  if (!token || token === previousToken) throw new Error('FCM token unchanged after instance deletion');

  // A new token starts without any topic: the remembered ones are stale and must be subscribed again.
  // With the same token they are kept, so the next syncs still unsubscribe the unwanted ones.
  localStorage.removeItem(SUBSCRIBED_TOPICS_KEY);
  localStorage.removeItem(LEGACY_SUBSCRIBED_TOPIC_KEY);
  localStorage.setItem(LEGACY_CLEANUP_DONE_KEY, '1');
}

/**
 * Align the device's FCM topics with `topics`, and with nothing else. Resolves to
 * false when a change failed, so the caller can undo what asked for it.
 *
 * The sync is gated so it never fails silently nor throws:
 * - Subscribing before the user granted notifications throws
 *   `Notifications permission is not granted`, so nothing is called without it.
 * - Subscribing before FCM finished registering makes the native plugin read
 *   `rel.topics` on an undefined response (`Cannot read properties of undefined
 *   (reading 'topics')`), so we await `getToken()` to confirm registration.
 * - Any failure (e.g. FCM `SERVICE_NOT_AVAILABLE`) is logged as an error, a real
 *   signal kept visible in Datadog, not filtered as noise. The stored set is
 *   updated topic by topic on success, so the next sync retries what failed.
 */
export async function syncFcmSubscriptions(topics: string[]): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return true;

  try {
    if ((await getNotificationPermission()) !== 'granted') return true;
    const { token } = await FCM.getToken();
    try {
      await cleanLegacySubscriptions(token);
    } catch (error) {
      // Not blocking: favorites still get their topics, the cleanup is retried next time.
      console.error('FCM legacy cleanup error:', error);
    }

    const subscribed = loadSubscribedTopics();
    const wanted = new Set(topics);

    for (const topic of [...subscribed].filter(topic => !wanted.has(topic))) {
      await FCM.unsubscribeFrom({ topic });
      subscribed.delete(topic);
      saveSubscribedTopics(subscribed);
    }
    for (const topic of wanted) {
      if (subscribed.has(topic)) continue;
      await FCM.subscribeTo({ topic });
      subscribed.add(topic);
      saveSubscribedTopics(subscribed);
    }
    return true;
  } catch (error) {
    console.error('FCM subscription error:', error);
    return false;
  }
}
